import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Modal,
  Platform,
  Dimensions,
} from 'react-native';
import Icon from 'react-native-vector-icons/MaterialIcons';
import Slider from './Slider';
import { useTheme } from '../contexts/ThemeContext';
import { getColors } from '../styles/theme';

const { width: SCREEN_WIDTH } = Dimensions.get('window');
const isMobile = SCREEN_WIDTH < 768;

const QuickPairSettingsModal = ({ visible, onClose, onStartSearch }) => {
  const { isDarkMode } = useTheme();
  const colors = getColors(isDarkMode);
  const [gameType, setGameType] = useState('RATED');
  const [timeFormat, setTimeFormat] = useState('BLITZ');
  const [maxDistance, setMaxDistance] = useState(5);

  const handleStartSearch = () => {
    onStartSearch({
      gameType,
      timeFormat,
      maxDistance,
    });
  };

  // Don't render anything if not visible (extra safety for web)
  if (!visible) {
    return null;
  }

  const timeFormats = [
    { key: 'BULLET', label: 'Bullet', subtitle: '1-2 min', icon: 'flash-on' },
    { key: 'BLITZ', label: 'Blitz', subtitle: '3-5 min', icon: 'timer' },
    { key: 'RAPID', label: 'Rapid', subtitle: '10-15 min', icon: 'access-time' },
    { key: 'CLASSICAL', label: 'Classical', subtitle: '30+ min', icon: 'hourglass-empty' },
  ];

  const sliderFillPercent = ((maxDistance - 1) / 9) * 100;

  return (
    <Modal
      visible={visible}
      animationType="slide"
      transparent={true}
      onRequestClose={onClose}
    >
      <>
      {Platform.OS === 'web' && (
        <style dangerouslySetInnerHTML={{ __html: `
          .quickpair-distance-slider {
            -webkit-appearance: none;
            appearance: none;
            width: 100%;
            height: 8px;
            border-radius: 4px;
            background: linear-gradient(to right, var(--slider-accent, #84D288) 0%, var(--slider-accent, #84D288) var(--slider-fill, 50%), var(--slider-track, #e5e7eb) var(--slider-fill), var(--slider-track, #e5e7eb) 100%);
          }
          .quickpair-distance-slider::-webkit-slider-thumb {
            -webkit-appearance: none;
            appearance: none;
            width: 22px;
            height: 22px;
            border-radius: 50%;
            background: var(--slider-accent, #84D288);
            cursor: pointer;
            box-shadow: 0 2px 8px rgba(0,0,0,0.15);
            border: 3px solid white;
          }
          .quickpair-distance-slider::-moz-range-thumb {
            width: 22px;
            height: 22px;
            border-radius: 50%;
            background: var(--slider-accent, #84D288);
            cursor: pointer;
            box-shadow: 0 2px 8px rgba(0,0,0,0.15);
            border: 3px solid white;
          }
          .quickpair-distance-slider::-moz-range-track {
            height: 8px;
            border-radius: 4px;
            background: var(--slider-track, #e5e7eb);
          }
          .quickpair-distance-slider::-moz-range-progress {
            height: 8px;
            border-radius: 4px 0 0 4px;
            background: var(--slider-accent, #84D288);
          }
        `}} />
      )}
      <View style={styles.modalOverlay}>
        <View style={[
          styles.modalContent,
          isMobile && styles.modalContentMobile,
          { backgroundColor: colors.surface }
        ]}>
          {/* Header */}
          <View style={[styles.header, isMobile && styles.headerMobile, { borderBottomColor: colors.border }]}>
            <Text style={[styles.headerTitle, isMobile && styles.headerTitleMobile, { color: colors.text }]}>Quick Pair</Text>
            <TouchableOpacity onPress={onClose} style={styles.closeButton}>
              <Icon name="close" size={isMobile ? 20 : 24} color={colors.text} />
            </TouchableOpacity>
          </View>

          <View style={[styles.popupContentNoScroll, isMobile && styles.popupContentMobile]}>
            {/* Game Type */}
            <View style={[styles.section, isMobile && styles.sectionMobile]}>
              <Text style={[styles.sectionTitle, isMobile && styles.sectionTitleMobile, { color: colors.text }]}>Game Type</Text>
              <View style={[styles.gameTypeContainer, isMobile && styles.gameTypeContainerMobile]}>
                <TouchableOpacity
                  style={[
                    styles.gameTypeButton,
                    isMobile && styles.gameTypeButtonMobile,
                    gameType === 'CASUAL' && styles.gameTypeButtonActive,
                  ]}
                  onPress={() => setGameType('CASUAL')}
                  activeOpacity={0.7}
                >
                  <Icon
                    name="emoji-emotions"
                    size={isMobile ? 18 : 22}
                    color={gameType === 'CASUAL' ? 'white' : 'rgba(255,255,255,0.75)'}
                  />
                  <Text
                    style={[
                      styles.gameTypeLabel,
                      isMobile && styles.gameTypeLabelMobile,
                      gameType === 'CASUAL' && styles.gameTypeLabelActive,
                    ]}
                  >
                    Casual
                  </Text>
                  <Text style={[
                    styles.gameTypeSubtitle,
                    isMobile && styles.gameTypeSubtitleMobile,
                    gameType === 'CASUAL' && styles.gameTypeSubtitleActive,
                  ]}>Just for fun</Text>
                </TouchableOpacity>

                <TouchableOpacity
                  style={[
                    styles.gameTypeButton,
                    isMobile && styles.gameTypeButtonMobile,
                    gameType === 'RATED' && styles.gameTypeButtonActive,
                  ]}
                  onPress={() => setGameType('RATED')}
                  activeOpacity={0.7}
                >
                  <Icon
                    name="military-tech"
                    size={isMobile ? 18 : 22}
                    color={gameType === 'RATED' ? 'white' : 'rgba(255,255,255,0.75)'}
                  />
                  <Text
                    style={[
                      styles.gameTypeLabel,
                      isMobile && styles.gameTypeLabelMobile,
                      gameType === 'RATED' && styles.gameTypeLabelActive,
                    ]}
                  >
                    Rated
                  </Text>
                  <Text style={[
                    styles.gameTypeSubtitle,
                    isMobile && styles.gameTypeSubtitleMobile,
                    gameType === 'RATED' && styles.gameTypeSubtitleActive,
                  ]}>Affects rating</Text>
                </TouchableOpacity>
              </View>
            </View>

            {/* Time Format */}
            <View style={[styles.section, isMobile && styles.sectionMobile]}>
              <Text style={[styles.sectionTitle, isMobile && styles.sectionTitleMobile, { color: colors.text }]}>Time Format</Text>
              <View style={[styles.timeFormatsContainer, isMobile && styles.timeFormatsContainerMobile]}>
                {timeFormats.map((format) => (
                  <TouchableOpacity
                    key={format.key}
                    style={[
                      styles.timeFormatButton,
                      isMobile && styles.timeFormatButtonMobile,
                      timeFormat === format.key && styles.timeFormatButtonActive,
                    ]}
                    onPress={() => setTimeFormat(format.key)}
                    activeOpacity={0.7}
                  >
                    <Icon
                      name={format.icon}
                      size={isMobile ? 20 : 24}
                      color={timeFormat === format.key ? 'white' : 'rgba(255,255,255,0.75)'}
                    />
                    <Text
                      style={[
                        styles.timeFormatLabel,
                        isMobile && styles.timeFormatLabelMobile,
                        timeFormat === format.key && styles.timeFormatLabelActive,
                      ]}
                    >
                      {format.label}
                    </Text>
                    <Text style={[
                      styles.timeFormatSubtitle,
                      isMobile && styles.timeFormatSubtitleMobile,
                      timeFormat === format.key && styles.timeFormatSubtitleActive,
                    ]}>{format.subtitle}</Text>
                  </TouchableOpacity>
                ))}
              </View>
            </View>

            {/* Max Distance */}
            <View style={[styles.section, isMobile && styles.sectionMobile]}>
              <View style={[styles.distanceHeader, isMobile && styles.distanceHeaderMobile]}>
                <Text style={[styles.sectionTitle, isMobile && styles.sectionTitleMobile, { color: colors.text }]}>Max Distance</Text>
                <View style={[styles.distanceValuePill, isMobile && styles.distanceValuePillMobile, { backgroundColor: isDarkMode ? 'rgba(132,210,136,0.25)' : 'rgba(132,210,136,0.2)' }]}>
                  <Icon name="location-on" size={isMobile ? 14 : 16} color="#84D288" />
                  <Text style={[styles.distanceValue, isMobile && styles.distanceValueMobile, { color: colors.text }]}>{maxDistance} km</Text>
                </View>
              </View>
              <View style={[styles.sliderWrapper, isMobile && styles.sliderWrapperMobile]}>
                <Slider
                  {...(Platform.OS === 'web' && {
                    className: 'quickpair-distance-slider',
                    fillPercent: sliderFillPercent,
                    accentColor: '#84D288',
                    trackColor: isDarkMode ? '#404040' : '#e5e7eb',
                  })}
                  style={styles.slider}
                  minimumValue={1}
                  maximumValue={10}
                  step={1}
                  value={maxDistance}
                  onValueChange={setMaxDistance}
                  {...(Platform.OS !== 'web' && {
                    minimumTrackTintColor: "#84D288",
                    maximumTrackTintColor: isDarkMode ? '#404040' : '#e5e7eb',
                    thumbTintColor: "#84D288",
                  })}
                />
                <View style={[styles.sliderLabels, isMobile && styles.sliderLabelsMobile]}>
                  <Text style={[styles.sliderLabel, { color: colors.textSecondary }]}>1 km</Text>
                  <Text style={[styles.sliderLabel, { color: colors.textSecondary }]}>10 km</Text>
                </View>
              </View>
            </View>
          </View>

          {/* Start Button */}
          <TouchableOpacity
            style={[styles.startButton, isMobile && styles.startButtonMobile]}
            onPress={handleStartSearch}
            activeOpacity={0.8}
          >
            <Icon name="person-search" size={isMobile ? 18 : 22} color="white" />
            <Text style={[styles.startButtonText, isMobile && styles.startButtonTextMobile]}>Start Searching</Text>
          </TouchableOpacity>
        </View>
      </View>
      </>
    </Modal>
  );
};

const styles = StyleSheet.create({
  popupContentNoScroll: {
    overflow: 'hidden',
    alignItems: 'center',
    paddingHorizontal: 4,
  },
  popupContentMobile: {
    flex: 1,
    paddingHorizontal: 2,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center', // Changed from 'flex-end' to center it on screen
    alignItems: 'center',
    zIndex: 20000,
    elevation: 50,
    ...(Platform.OS === 'web' ? {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      zIndex: 20000,
    } : {}),
  },
  modalContent: {
    borderRadius: 20,
    overflow: 'hidden',
    paddingTop: 16,
    paddingBottom: Platform.OS === 'ios' ? 28 : 16,
    maxHeight: '88%',
    width: '88%',
    maxWidth: 400,
    ...(Platform.OS === 'web' ? {
      marginHorizontal: 'auto',
      maxHeight: '88vh',
    } : {}),
  },
  modalContentMobile: {
    maxHeight: '85%',
    paddingTop: 12,
    paddingBottom: Platform.OS === 'ios' ? 24 : 12,
    ...(Platform.OS === 'web' ? { maxHeight: '85vh' } : {}),
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingBottom: 12,
    borderBottomWidth: 1,
  },
  headerMobile: {
    paddingHorizontal: 12,
    paddingBottom: 10,
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: 'bold',
  },
  headerTitleMobile: {
    fontSize: 18,
  },
  closeButton: {
    padding: 4,
  },
  section: {
    width: '100%',
    maxWidth: 340,
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  sectionMobile: {
    maxWidth: 300,
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: '600',
    marginBottom: 10,
  },
  sectionTitleMobile: {
    fontSize: 13,
    marginBottom: 8,
  },
  gameTypeContainer: {
    flexDirection: 'row',
    gap: 10,
    justifyContent: 'center',
  },
  gameTypeContainerMobile: {
    gap: 8,
  },
  gameTypeButton: {
    width: '47%',
    maxWidth: 140,
    alignItems: 'center',
    paddingVertical: 10,
    paddingHorizontal: 12,
    borderRadius: 10,
    backgroundColor: '#84D288',
    shadowColor: '#76AF87',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 1,
    shadowRadius: 0,
    elevation: 8,
  },
  gameTypeButtonMobile: {
    maxWidth: 120,
    paddingVertical: 8,
    paddingHorizontal: 8,
    borderRadius: 8,
  },
  gameTypeButtonActive: {
    backgroundColor: '#4E9B52',
    shadowColor: '#3d7a40',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 1,
    shadowRadius: 0,
    transform: [{ translateY: 2 }],
  },
  gameTypeLabel: {
    fontSize: 14,
    fontWeight: '700',
    color: 'rgba(255, 255, 255, 0.75)',
    marginTop: 4,
  },
  gameTypeLabelMobile: {
    fontSize: 12,
    marginTop: 2,
  },
  gameTypeLabelActive: {
    color: 'white',
  },
  gameTypeSubtitle: {
    fontSize: 10,
    color: 'rgba(255, 255, 255, 0.7)',
    marginTop: 2,
  },
  gameTypeSubtitleMobile: {
    fontSize: 9,
    marginTop: 0,
  },
  gameTypeSubtitleActive: {
    color: 'rgba(255, 255, 255, 0.95)',
  },
  timeFormatsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    justifyContent: 'center',
  },
  timeFormatsContainerMobile: {
    gap: 6,
  },
  timeFormatButton: {
    width: '47%',
    maxWidth: 140,
    alignItems: 'center',
    paddingVertical: 10,
    paddingHorizontal: 8,
    borderRadius: 8,
    backgroundColor: '#84D288',
    shadowColor: '#76AF87',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 1,
    shadowRadius: 0,
    elevation: 8,
  },
  timeFormatButtonMobile: {
    maxWidth: 120,
    paddingVertical: 8,
    paddingHorizontal: 6,
  },
  timeFormatButtonActive: {
    backgroundColor: '#4E9B52',
    shadowColor: '#3d7a40',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 1,
    shadowRadius: 0,
    transform: [{ translateY: 2 }],
  },
  timeFormatLabel: {
    fontSize: 12,
    fontWeight: '700',
    color: 'rgba(255, 255, 255, 0.75)',
    marginTop: 4,
  },
  timeFormatLabelMobile: {
    fontSize: 11,
    marginTop: 2,
  },
  timeFormatLabelActive: {
    color: 'white',
  },
  timeFormatSubtitle: {
    fontSize: 10,
    color: 'rgba(255, 255, 255, 0.7)',
    marginTop: 2,
  },
  timeFormatSubtitleMobile: {
    fontSize: 9,
    marginTop: 0,
  },
  timeFormatSubtitleActive: {
    color: 'rgba(255, 255, 255, 0.95)',
  },
  distanceHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  distanceHeaderMobile: {
    marginBottom: 6,
  },
  distanceValuePill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 20,
  },
  distanceValuePillMobile: {
    paddingHorizontal: 8,
    paddingVertical: 5,
    gap: 3,
  },
  distanceValue: {
    fontSize: 14,
    fontWeight: '600',
  },
  distanceValueMobile: {
    fontSize: 13,
  },
  sliderWrapper: {
    paddingVertical: 8,
    paddingHorizontal: 4,
  },
  sliderWrapperMobile: {
    paddingVertical: 6,
  },
  slider: {
    width: '100%',
    height: Platform.OS === 'web' ? 8 : 40,
  },
  sliderLabels: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 6,
    paddingHorizontal: 2,
  },
  sliderLabelsMobile: {
    marginTop: 5,
  },
  sliderLabel: {
    fontSize: 11,
    fontWeight: '500',
  },
  startButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    alignSelf: 'center',
    gap: 6,
    backgroundColor: '#84D288',
    marginHorizontal: 16,
    marginTop: 8,
    minWidth: 200,
    maxWidth: 280,
    paddingVertical: 12,
    paddingHorizontal: 14,
    borderRadius: 10,
    shadowColor: '#76AF87',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 1,
    shadowRadius: 0,
    elevation: 8,
  },
  startButtonMobile: {
    marginHorizontal: 12,
    marginTop: 6,
    marginBottom: 4,
    minWidth: 180,
    maxWidth: 260,
    paddingVertical: 10,
    paddingHorizontal: 12,
    borderRadius: 8,
  },
  startButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: 'white',
  },
  startButtonTextMobile: {
    fontSize: 13,
  },
});

export default QuickPairSettingsModal;

